import {
    mdiAt,
    mdiDownload,
    mdiEmailArrowRightOutline,
    mdiEmailOutline,
    mdiFax,
    mdiFileDocumentOutline,
    mdiOpenInNew,
    mdiPhoneOutline,
    mdiRobotOutline,
    mdiSwapHorizontal,
} from '@mdi/js';
import Icon from '@mdi/react';
import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { fileCabinetDocumentTypeLabel } from '../../services/file-cabinet/file-cabinet.document-types';
import type { FileCabinetReference, FileCabinetReferences } from '../../services/file-cabinet/file-cabinet.references';
import type {
    CabinetAiCall,
    CabinetCall,
    CabinetDocument,
    CabinetEmail,
    CabinetLetter,
    CabinetMail,
    CabinetReceivedFax,
    CabinetSentFax,
    FileCabinetDataSource,
    FileCabinetFileUrls,
    FileCabinetRecord,
    FileCabinetViewKey,
} from '../../services/file-cabinet/file-cabinet.types';
import { fileCabinetRecordId } from '../../services/file-cabinet/file-cabinet.types';
import { formatBytes } from '../../services/primitives/number-format';
import { formatDate } from '../../services/primitives/date-utils';
import { FileCabinetStore } from '../../stores/file-cabinet/file-cabinet.store';
import { ClaimIdBadge } from '../badge/claim-id-badge';
import { BaseButton } from '../base/button/button';
import { DetailField, DetailSection } from '../base/detail-panel/detail-panel';
import { DrawerSheet } from '../drawer-sheet/drawer-sheet';
import { Spinner } from '../base/spinner/spinner';
import { DocumentViewer } from '../document-viewer/document-viewer';
import {
    callDuration,
    claimsOf,
    documentOf,
    fileCabinetFileName,
    hasTime,
    letterEvents,
    letterStatus,
    methodLabel,
} from '../../services/file-cabinet/file-cabinet.records';
import { DocumentTypeTag, EmailStatus, FaxResult, phone } from './file-cabinet-cells';
import {
    AiCallGaps,
    CallRecording,
    DerivedDocuments,
    DocumentRow,
    EmailMessage,
    ReferencedBy,
    SourceDocuments,
} from './file-cabinet-drawer-sections';
import * as S from './file-cabinet.styles';

/** With its time when the value has one; a date-only value stays a date (see `hasTime`). */
const dateTime = (value?: string): string | undefined =>
    value ? formatDate(value, hasTime(value) ? 'MMM d, yyyy h:mm a' : 'MMM d, yyyy') : undefined;
const dateOnly = (value?: string): string | undefined => (value ? formatDate(value, 'MMM d, yyyy') : undefined);

/** The record drawer (FE-225): what the record says, and the file it carried. */
export const FileCabinetDrawer = observer(({ store }: { store: FileCabinetStore }): React.ReactElement | null => {
    const drawer = store.drawer;
    if (!drawer) {
        return null;
    }
    // Keyed by record: a new record starts with a fresh file lookup, never the last one's URL.
    return (
        <DrawerBody
            key={`${drawer.view}:${fileCabinetRecordId(drawer.view, drawer.record)}`}
            view={drawer.view}
            record={drawer.record}
            dataSource={store.dataSource}
            onClose={store.closeDrawer}
            onOpenDocument={store.openDocument}
            onOpenReference={store.openReference}
            loadReferences={store.referencesOf}
        />
    );
});

interface IDrawerBodyProps {
    view: FileCabinetViewKey;
    record: FileCabinetRecord;
    dataSource: FileCabinetDataSource;
    onClose: () => void;
    onOpenDocument: (document: CabinetDocument) => void;
    onOpenReference: (reference: FileCabinetReference) => void;
    loadReferences: (documentIds: string[], limit: number) => Promise<FileCabinetReferences>;
}

function DrawerBody({
    view,
    record,
    dataSource,
    onClose,
    onOpenDocument,
    onOpenReference,
    loadReferences,
}: IDrawerBodyProps): React.ReactElement {
    // Asked once per record: the header buttons, the preview and a call's player all read this answer.
    const urls = useMemo(() => dataSource.fileUrls(view, record), [dataSource, view, record]);
    const file = useFileUrl(urls);
    const header = headerFor(view, record);
    const fileName = fileCabinetFileName(view, record);

    return (
        <DrawerSheet
            iconPath={header.icon}
            title={header.title}
            subtitle={header.subtitle}
            onClose={onClose}
            actions={
                file.status === 'ready' && file.urls ? (
                    <>
                        <BaseButton
                            variant="primary"
                            size="compact"
                            onClick={(): void => downloadFile((file.urls as FileCabinetFileUrls).download, fileName)}
                        >
                            <S.ButtonContent>
                                <Icon path={mdiDownload} size={0.7} />
                                Download
                            </S.ButtonContent>
                        </BaseButton>
                        <BaseButton
                            variant="outlined"
                            size="compact"
                            onClick={(): void => void window.open((file.urls as FileCabinetFileUrls).view, '_blank', 'noopener')}
                        >
                            <S.ButtonContent>
                                <Icon path={mdiOpenInNew} size={0.7} />
                                Open in New Tab
                            </S.ButtonContent>
                        </BaseButton>
                    </>
                ) : undefined
            }
        >
            <DetailSection title="Details">
                {fieldsFor(view, record)}
                {supportFieldsFor(record)}
            </DetailSection>
            {sectionsFor(view, record, dataSource, urls, onOpenDocument, onOpenReference, loadReferences)}
            {hasPreview(view) && (
                <DetailSection title={view === 'mail.out' ? 'Letter as printed' : view === 'fax.out' ? 'Fax as sent' : 'Preview'}>
                    <Preview file={file} fileName={fileName} />
                </DetailSection>
            )}
        </DrawerSheet>
    );
}

/** A call's file is a recording, played by its own section; an AI call has none. */
function hasPreview(view: FileCabinetViewKey): boolean {
    return !view.startsWith('call.');
}

/** What each record shows between its fields and its file. */
function sectionsFor(
    view: FileCabinetViewKey,
    record: FileCabinetRecord,
    dataSource: FileCabinetDataSource,
    urls: Promise<FileCabinetFileUrls | undefined>,
    onOpenDocument: (document: CabinetDocument) => void,
    onOpenReference: (reference: FileCabinetReference) => void,
    loadReferences: (documentIds: string[], limit: number) => Promise<FileCabinetReferences>,
): React.ReactElement {
    const claims = (
        <DetailSection title="Claims">
            <Claims ids={claimsOf(view, record)} />
        </DetailSection>
    );
    switch (view) {
        case 'docs': {
            const document = record as CabinetDocument;
            return (
                <>
                    {claims}
                    <ReferencedBy document={document} load={loadReferences} onOpen={onOpenReference} />
                    <SourceDocuments document={document} dataSource={dataSource} onOpen={onOpenDocument} />
                    <DerivedDocuments document={document} dataSource={dataSource} onOpen={onOpenDocument} />
                </>
            );
        }
        case 'email.out':
            return (
                <>
                    <EmailMessage contents={(record as CabinetEmail).contents} />
                    <CarriedDocument document={documentOf(record)} view={view} onOpen={onOpenDocument} />
                    {claims}
                </>
            );
        case 'call.in':
        case 'call.out':
        case 'call.other':
            return (
                <>
                    <CallRecording call={record as CabinetCall} urls={urls} dataSource={dataSource} />
                    {claims}
                </>
            );
        case 'call.ai':
            return <AiCallGaps />;
        case 'mail.in':
        case 'mail.out':
        case 'fax.in':
        case 'fax.out':
            return (
                <>
                    <CarriedDocument document={documentOf(record)} view={view} onOpen={onOpenDocument} />
                    {view === 'mail.out' && <TrackingEvents letter={record as CabinetLetter} />}
                    {claims}
                </>
            );
        default:
            return unreachable(view);
    }
}

/* ── the file ─────────────────────────────────────────────────── */

type FileState = { status: 'loading' | 'none' | 'ready' | 'error'; urls?: FileCabinetFileUrls; message?: string };

/**
 * The record's file links, looked up when the drawer opens — both of them, so "Open in New Tab"
 * opens synchronously on the click (a tab opened after an `await` is blocked as a pop-up). No file is
 * "none", never an error.
 */
function useFileUrl(urls: Promise<FileCabinetFileUrls | undefined>): FileState {
    const [state, setState] = useState<FileState>({ status: 'loading' });
    useEffect(() => {
        let live = true;
        setState({ status: 'loading' });
        urls.then(
            urls => live && setState(urls ? { status: 'ready', urls } : { status: 'none' }),
            (error: unknown) =>
                live && setState({ status: 'error', message: error instanceof Error ? error.message : String(error) }),
        );
        return (): void => {
            live = false;
        };
    }, [urls]);
    return state;
}

function Preview({ file, fileName }: { file: FileState; fileName?: string }): React.ReactElement {
    switch (file.status) {
        case 'loading':
            return (
                <S.Preview>
                    <S.PreviewNote>
                        <Spinner />
                        Loading the file…
                    </S.PreviewNote>
                </S.Preview>
            );
        case 'none':
            return (
                <S.Preview>
                    <S.PreviewNote>No file is attached to this record.</S.PreviewNote>
                </S.Preview>
            );
        case 'error':
            return (
                <S.Preview>
                    <S.PreviewNote>The file link could not be loaded. {file.message}</S.PreviewNote>
                </S.Preview>
            );
        default:
            return (
                <S.Preview>
                    <DocumentViewer url={(file.urls as FileCabinetFileUrls).download} documentName={fileName} />
                </S.Preview>
            );
    }
}

/**
 * Navigation, not fetch. The browser ignores `download` on a cross-origin link, so whether this saves
 * or opens the file is the server's `Content-Disposition` — document links are served as attachments.
 */
function downloadFile(url: string, name?: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = name || 'document';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

/* ── header and fields, per view ──────────────────────────────── */

function headerFor(view: FileCabinetViewKey, record: FileCabinetRecord): { icon: string; title: string; subtitle?: string } {
    switch (view) {
        case 'docs': {
            const document = record as CabinetDocument;
            return {
                icon: mdiFileDocumentOutline,
                title: document.filename || 'No filename',
                subtitle: [fileCabinetDocumentTypeLabel(document.documentType), dateOnly(document.created)].filter(Boolean).join(' · '),
            };
        }
        case 'mail.in': {
            const mail = record as CabinetMail;
            return {
                icon: mdiEmailOutline,
                title: mail.barcode ? `Mail ${mail.barcode}` : 'Received mail',
                subtitle: ['Received mail', dateTime(mail.receivedDate)].filter(Boolean).join(' · '),
            };
        }
        case 'mail.out': {
            const letter = record as CabinetLetter;
            const to = letter.response?.to;
            return {
                icon: mdiEmailArrowRightOutline,
                title: to?.company || to?.name || 'Letter',
                subtitle: ['Letter', dateTime(letter.created) && `created ${dateTime(letter.created)}`].filter(Boolean).join(' · '),
            };
        }
        case 'fax.in': {
            const fax = record as CabinetReceivedFax;
            return {
                icon: mdiFax,
                title: phone(fax.fromE164) || 'Received fax',
                subtitle: ['Received fax', dateTime(fax.received)].filter(Boolean).join(' · '),
            };
        }
        case 'email.out': {
            const email = record as CabinetEmail;
            return {
                icon: mdiAt,
                title: email.subject || '(no subject)',
                subtitle: ['Sent email', dateTime(email.created)].filter(Boolean).join(' · '),
            };
        }
        case 'call.in':
        case 'call.out':
        case 'call.other': {
            const call = record as CabinetCall;
            return {
                icon: view === 'call.other' ? mdiSwapHorizontal : mdiPhoneOutline,
                title: phone(call.phoneE164) || 'Call',
                subtitle: [`${methodLabel(call.method) ?? 'Unknown method'} call`, dateTime(call.start)].filter(Boolean).join(' · '),
            };
        }
        case 'call.ai': {
            const call = record as CabinetAiCall;
            return {
                icon: mdiRobotOutline,
                title: phone(call.number) || 'AI call',
                subtitle: ['AI call', dateTime(call.created)].filter(Boolean).join(' · '),
            };
        }
        case 'fax.out': {
            const fax = record as CabinetSentFax;
            return {
                icon: mdiFax,
                title: phone(fax.toE164) || 'Sent fax',
                subtitle: ['Sent fax', dateTime(fax.sent)].filter(Boolean).join(' · '),
            };
        }
        default:
            return unreachable(view);
    }
}

/** A view with no case is a compile error — never a record rendered as some other kind. */
function unreachable(view: never): never {
    throw new Error(`The File Cabinet drawer has no case for the view "${String(view)}"`);
}

/**
 * What only the support API returns — tags, the assigned/starred/active flags, a document's notes. Each
 * shown only when the record carries it, so a provider record (which never does) shows none of them,
 * rather than a row of "Not available".
 */
function supportFieldsFor(record: FileCabinetRecord): React.ReactElement | null {
    const extra = record as { tags?: { id?: string; name?: string }[]; assigned?: boolean; starred?: boolean; active?: boolean; notes?: string };
    const yesNo = (value: boolean): string => (value ? 'Yes' : 'No');
    const fields = [
        extra.tags !== undefined && (
            <DetailField
                key="tags"
                label="Tags"
                value={
                    extra.tags.length ? (
                        <S.ClaimList>
                            {extra.tags.map((tag, index) => (
                                <S.Chip key={tag.id ?? index} tone="neutral">
                                    {tag.name || 'Unnamed tag'}
                                </S.Chip>
                            ))}
                        </S.ClaimList>
                    ) : (
                        'None'
                    )
                }
            />
        ),
        extra.assigned !== undefined && <DetailField key="assigned" label="Assigned" value={yesNo(extra.assigned)} />,
        extra.starred !== undefined && <DetailField key="starred" label="Starred" value={yesNo(extra.starred)} />,
        extra.active !== undefined && <DetailField key="active" label="Active" value={yesNo(extra.active)} />,
        extra.notes !== undefined && (
            // Free text about a patient's document: blocked from session replay.
            <DetailField key="notes" label="Notes" value={extra.notes ? <span data-sentry-block>{extra.notes}</span> : undefined} absent="None" />
        ),
    ].filter(Boolean);
    return fields.length ? <>{fields}</> : null;
}

function fieldsFor(view: FileCabinetViewKey, record: FileCabinetRecord): React.ReactElement {
    switch (view) {
        case 'docs': {
            const document = record as CabinetDocument;
            return (
                <>
                    <DetailField label="Type" value={<DocumentTypeTag type={document.documentType} />} />
                    <DetailField label="Description" value={document.description} />
                    <DetailField label="Size" value={document.filesize ? formatBytes(document.filesize) : undefined} />
                    <DetailField label="Created" value={dateTime(document.created)} />
                </>
            );
        }
        case 'mail.in': {
            const mail = record as CabinetMail;
            return (
                <>
                    <DetailField label="Barcode" value={mail.barcode && <S.Mono>{mail.barcode}</S.Mono>} />
                    <DetailField label="Item type" value={mail.itemType} />
                    <DetailField label="Type" value={mail.type} />
                    <DetailField label="Pages" value={mail.pages} />
                    <DetailField label="Checks" value={mail.checks} />
                    {/* The API gives no unit for weight, so none is invented (NS-4). */}
                    <DetailField label="Weight" value={mail.weight} />
                    <DetailField label="Received" value={dateTime(mail.receivedDate)} />
                    <DetailField label="Scanned" value={dateTime(mail.scanDate)} />
                </>
            );
        }
        case 'mail.out': {
            const letter = record as CabinetLetter;
            const response = letter.response;
            const to = response?.to;
            const address = to
                ? [to.company || to.name, to.addressLineOne, to.addressLineTwo, [to.city, to.state, to.zip].filter(Boolean).join(' ')]
                      .filter(Boolean)
                      .join('\n')
                : undefined;
            return (
                <>
                    <DetailField label="Last event" value={<S.Chip tone="neutral">{letterStatus(letter)}</S.Chip>} />
                    <DetailField label="To" value={address && <S.Address>{address}</S.Address>} />
                    <DetailField label="Service" value={[response?.mailType, response?.extraService].filter(Boolean).join(' · ')} />
                    <DetailField
                        label="Tracking"
                        value={[response?.carrier, response?.trackingNumber].filter(Boolean).join(' ')}
                    />
                    <DetailField label="Send date" value={dateOnly(response?.sendDate)} />
                    <DetailField label="Expected" value={dateOnly(response?.expectedDeliveryDate)} />
                    <DetailField label="Created" value={dateTime(letter.created)} />
                </>
            );
        }
        case 'fax.in': {
            const fax = record as CabinetReceivedFax;
            return (
                <>
                    <DetailField label="From" value={phone(fax.fromE164)} />
                    <DetailField label="To" value={phone(fax.toE164)} />
                    <DetailField label="Pages" value={fax.pages} />
                    <DetailField label="External ID" value={fax.externalId && <S.Mono>{fax.externalId}</S.Mono>} />
                    <DetailField label="Received" value={dateTime(fax.received)} />
                </>
            );
        }
        case 'email.out': {
            const email = record as CabinetEmail;
            return (
                <>
                    <DetailField label="Status" value={<EmailStatus status={email.status} />} />
                    <DetailField label="To" value={[email.toName, email.toEmail && `<${email.toEmail}>`].filter(Boolean).join(' ')} />
                    <DetailField label="From" value={[email.fromName, email.fromEmail && `<${email.fromEmail}>`].filter(Boolean).join(' ')} />
                    {/* `created` is when the record was made; the API has no separate send time. */}
                    <DetailField label="Created" value={dateTime(email.created)} />
                </>
            );
        }
        case 'call.in':
        case 'call.out':
        case 'call.other': {
            const call = record as CabinetCall;
            return (
                <>
                    <DetailField label="Method" value={methodLabel(call.method)} />
                    <DetailField label="Channel" value={methodLabel(call.channel)} />
                    <DetailField label="Number" value={phone(call.phoneE164)} />
                    <DetailField label="Agent" value={call.agent} />
                    <DetailField label="Started" value={dateTime(call.start)} />
                    <DetailField label="Ended" value={dateTime(call.end)} />
                    <DetailField label="Duration" value={callDuration(call.start, call.end)} />
                </>
            );
        }
        case 'call.ai': {
            const call = record as CabinetAiCall;
            return (
                <>
                    {/* Free strings in the API: shown as sent, never coloured or interpreted (NS-4). */}
                    <DetailField label="Status" value={call.status && <S.Chip tone="neutral">{call.status}</S.Chip>} />
                    <DetailField label="Number" value={phone(call.number)} />
                    <DetailField label="Script ID" value={call.scriptId && <S.Mono>{call.scriptId}</S.Mono>} />
                    <DetailField label="External ID" value={call.externalId && <S.Mono>{call.externalId}</S.Mono>} />
                    <DetailField label="Created" value={dateTime(call.created)} />
                    {/* No date says nothing about the call's state; the status above says that. */}
                    <DetailField label="Completed" value={dateTime(call.completedDate)} />
                </>
            );
        }
        case 'fax.out': {
            const fax = record as CabinetSentFax;
            return (
                <>
                    <DetailField label="Result" value={<FaxResult fax={fax} />} />
                    <DetailField label="Result detail" value={fax.result} />
                    <DetailField label="To" value={phone(fax.toE164)} />
                    <DetailField label="From" value={phone(fax.fromE164)} />
                    <DetailField label="Pages" value={fax.pages} />
                    <DetailField label="External ID" value={fax.externalId && <S.Mono>{fax.externalId}</S.Mono>} />
                    <DetailField label="Sent" value={dateTime(fax.sent)} />
                    {/* The API documents only "this fax was a retried fax", not which way the link points — so the
                        field's own name, not "Retry of" or "Retried as". */}
                    <DetailField label="Retried fax ID" value={fax.retriedFaxId && <S.Mono>{fax.retriedFaxId}</S.Mono>} />
                </>
            );
        }
        default:
            return unreachable(view);
    }
}

/**
 * The document a mail, letter or fax record carries — opened in All Documents, where its own record,
 * claims and file live. For a letter it is the document the letter MAILED; the file shown below is the
 * letter as printed.
 */
function CarriedDocument({
    document,
    view,
    onOpen,
}: {
    document: CabinetDocument | undefined;
    view: FileCabinetViewKey;
    onOpen: (document: CabinetDocument) => void;
}): React.ReactElement {
    const title =
        view === 'mail.out' ? 'Document mailed' : view === 'fax.out' ? 'Document faxed' : view === 'email.out' ? 'Attachment' : 'Document';
    return (
        <DetailSection title={title}>
            {document?.id ? (
                <DocumentRow document={document} onOpen={onOpen} />
            ) : (
                <S.Absent>{document ? 'The document has no ID to open.' : 'No document came with this record.'}</S.Absent>
            )}
        </DetailSection>
    );
}

function Claims({ ids }: { ids: string[] | undefined }): React.ReactElement {
    if (ids === undefined) {
        // Not "none": the claims were not part of what the API returned for this record.
        return <S.Absent>Unknown — claims were not returned with this record</S.Absent>;
    }
    if (ids.length === 0) {
        return <S.Absent>Not attached to a claim</S.Absent>;
    }
    return (
        <S.ClaimList>
            {ids.map(id => (
                <ClaimIdBadge key={id} claimId={id} />
            ))}
        </S.ClaimList>
    );
}

/** Newest first. Event names are free strings in the API, shown as sent (NS-4). */
function TrackingEvents({ letter }: { letter: CabinetLetter }): React.ReactElement {
    const events = letterEvents(letter);
    return (
        <DetailSection title="Tracking events">
            {events.length === 0 ? (
                <S.Absent>{letter.cancelled ? 'Cancelled before mailing.' : 'No events yet.'}</S.Absent>
            ) : (
                <S.Timeline>
                    {events.map((event, index) => (
                        <S.TimelineItem key={`${event.time}-${index}`}>
                            <S.TimelineRail>
                                <S.TimelineDot latest={index === 0} />
                                {index < events.length - 1 && <S.TimelineLine />}
                            </S.TimelineRail>
                            <S.TimelineBody>
                                <S.TimelineName>{event.name || 'Unnamed event'}</S.TimelineName>
                                <S.TimelineMeta>
                                    {[event.location, dateTime(event.time)].filter(Boolean).join(' · ')}
                                </S.TimelineMeta>
                            </S.TimelineBody>
                        </S.TimelineItem>
                    ))}
                </S.Timeline>
            )}
        </DetailSection>
    );
}
